import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';
import { setInteriorFloor } from '../state/interiorFloor';

// RoomCollider — the SOLID twin of a splat room.
//
// Marble returns a collider mesh alongside every world's splats: real geometry
// of the same reconstruction (ours is ~249k triangles WITH vertex colors, no
// textures). The splat is all canvas and no body — nothing to raycast, so a
// splat room has box bounds and no true floor. This mounts the collider in the
// EXACT transform the splat was fitted with, invisible, purely to be measured
// against: FloorClamp gets a floor to stand on (stairs, the desk you can't
// stand inside), and later passes get occlusion/physics geometry for free.
//
// ── ALIGNMENT ───────────────────────────────────────────────────────────────
// SplatWorld fits the splat with: 180°-about-X flip (spz convention is +Y
// DOWN), metric scale, then a translation derived from percentile splat stats
// (fitSplatToScene). It now publishes that {position, scale} via onLoaded, and
// this component applies the SAME numbers. The one open question per source is
// the flip: a GLB is +Y-up BY SPEC, so if Marble pre-flipped the collider (the
// likely case) it must NOT be rotated here — the scale+translation land it.
// If a collider arrives in raw splat space instead, ?colliderflip=1 applies
// the same 180°-X the splat gets. Judge with the debug view:
//
//   ?collider=1       draw it (vertex colors, translucent) over the splat —
//                     aligned means walls sit ON walls
//   ?colliderflip=1   the other Y convention, if the room draws upside-down
//   ?walk=1           publish it to FloorClamp (experimental: the clamp
//                     ground-follows at the world's 0.75 eye height, which in
//                     a metric-scale scanned room reads low — parked until
//                     the room's own eye height is decided)
export default function RoomCollider({ url, fit }) {
  const { scene } = useGLTF(url);
  const q = useMemo(() => new URLSearchParams(window.location.search), []);
  const debugVisible = q.get('collider') === '1';
  const flip = q.get('colliderflip') === '1';
  const feedFloor = q.get('walk') === '1';

  const group = useMemo(() => {
    if (!fit) return null;
    const g = new THREE.Group();
    const m = scene.clone(true);
    // One material policy for every mesh: invisible-but-raycastable by
    // default; the debug view shows the vertex-colored surface from both
    // sides so misalignment is readable from inside the room.
    m.traverse((o) => {
      if (!o.isMesh) return;
      o.material = new THREE.MeshBasicMaterial({
        vertexColors: !!o.geometry?.attributes?.color,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
        side: THREE.DoubleSide,
        // The load-bearing trick: an invisible MATERIAL is skipped by the
        // renderer (no draw of 249k tris) but Mesh.raycast intersects the
        // geometry regardless — solid to FloorClamp, free to the GPU.
        visible: debugVisible,
      });
    });
    if (flip) m.quaternion.set(1, 0, 0, 0);
    g.add(m);
    g.scale.setScalar(fit.scale);
    g.position.set(...fit.position);
    return g;
  }, [scene, fit, debugVisible, flip, feedFloor]);

  // The floor probe. Splat rooms have never had one — CameraBounds boxes them
  // in, but height is a constant. Publishing the collider gives FloorClamp
  // real ground. Opt-in (?walk=1) until the eye-height question is settled.
  useEffect(() => {
    if (!feedFloor || !group) return undefined;
    setInteriorFloor(group);
    return () => setInteriorFloor(null);
  }, [feedFloor, group]);

  useEffect(() => {
    if (!group || !debugVisible) return;
    const box = new THREE.Box3().setFromObject(group);
    console.log(`[collider] ${url} — world box `
      + `x ${box.min.x.toFixed(2)}..${box.max.x.toFixed(2)} · `
      + `y ${box.min.y.toFixed(2)}..${box.max.y.toFixed(2)} · `
      + `z ${box.min.z.toFixed(2)}..${box.max.z.toFixed(2)} `
      + `(scale ${fit?.scale?.toFixed?.(3)}; flip ${flip ? 'ON' : 'off'}). `
      + 'Aligned = its walls sit on the splat walls; upside-down = toggle ?colliderflip=1.');
  }, [group, debugVisible, url, fit, flip]);

  if (!group) return null;
  return <primitive object={group} />;
}
